import { useTranslation } from 'react-i18next';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ConfirmFileUploadView from 'verify/components/ConfirmFileUploadView';
import ImageOriented from 'verify/components/ImageOriented';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const ImagePositioner = styled('div')`
  img {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    max-height: 320px;

    margin-bottom: ${SPACINGS.base * 2}px;
  }
`;

const Minify = styled('div')`
  display: inline;

  img {
    height: 12px;
    margin-bottom: 4px;
  }
`;

interface VerifyGovernmentIdCheckViewProps {
  currentUpload: StoredFileUpload;
  isProcessing: boolean;
  handleContinue: () => void;
  handleRetry: () => void;
}

export default function VerifyGovernmentIdCheckView(props: VerifyGovernmentIdCheckViewProps) {
  const { currentUpload, handleContinue, handleRetry, isProcessing } = props;

  const [t] = useTranslation();

  if (!['image/jpeg', 'image/png'].includes(currentUpload.fileType)) {
    return (
      <ConfirmFileUploadView
        fileName={currentUpload.filename}
        isProcessing={isProcessing}
        handleContinue={handleContinue}
        handleRetry={handleRetry}
      />
    );
  }

  return (
    <div>
      <Header>
        <ThemedTitle>{t('government-id.check.title')}</ThemedTitle>
        <ThemedParagraph>{t('government-id.check.prompt')}</ThemedParagraph>
      </Header>

      <ImagePositioner>
        <ImageOriented src={currentUpload.dataUrl} alt='upload preview' />
      </ImagePositioner>

      <PositionableButtonContainer>
        <Button
          id='government_id__use-image'
          buttonType={ButtonType.Primary}
          disabled={isProcessing}
          onClick={handleContinue}
          data-test='government-id-check__continue'
        >
          {isProcessing ? (
            <span>
              {t('government-id.check.uploading')}
              <Minify>
                <InlineLoadingIndicator />
              </Minify>
            </span>
          ) : (
            t('government-id.check.btn-submit')
          )}
        </Button>

        <Spacer height={2} />

        <Button
          id='government_id__retake'
          buttonType={ButtonType.Secondary}
          disabled={isProcessing}
          onClick={handleRetry}
          data-test='government-id-check__retake'
        >
          {t('government-id.check.btn-retake')}
        </Button>
      </PositionableButtonContainer>
    </div>
  );
}
